import { MAX_COMMAND_RANK, SAVE_KEY, SAVE_VERSION, scaleDataReward, TEST_UNLOCK_ALL } from '../config/constants';
import { TOWER_ORDER } from '../data/towers';

const SLOT_PREFIX = 'apogee.slot.';
const PROFILE_PREFIX = 'apogee.profile.';
const PROFILE_INDEX_KEY = 'apogee.profiles';
export const SLOT_COUNT = 3;

/** DATA is a spendable whole-point currency; never persist fractional points. */
export function normalizeResearchPoints(amount: number): number {
  return Math.ceil(Math.max(0, amount));
}

export interface CommandRank {
  title: string;
  abbreviation: string;
  min: number;
  max: number;
}

/** Human-readable command ladder used anywhere a save rank is shown. */
export const COMMAND_RANKS: CommandRank[] = [
  { title: 'CORPORAL', abbreviation: 'CPL', min: 1, max: 4 },
  { title: 'SERGEANT', abbreviation: 'SGT', min: 5, max: 9 },
  { title: 'LIEUTENANT', abbreviation: 'LT', min: 10, max: 14 },
  { title: 'CAPTAIN', abbreviation: 'CPT', min: 15, max: 19 },
  { title: 'MAJOR', abbreviation: 'MAJ', min: 20, max: 24 },
  { title: 'COLONEL', abbreviation: 'COL', min: 25, max: 29 },
  { title: 'BRIGADIER', abbreviation: 'BRIG', min: 30, max: 34 },
  { title: 'MAJOR GENERAL', abbreviation: 'MG', min: 35, max: 39 },
  { title: 'GENERAL', abbreviation: 'GEN', min: 40, max: Number.POSITIVE_INFINITY },
];

export function getCommandRank(rank: number): CommandRank {
  const safeRank = Math.max(1, Math.floor(rank));
  return COMMAND_RANKS.find((band) => safeRank >= band.min && safeRank <= band.max) ?? COMMAND_RANKS[COMMAND_RANKS.length - 1];
}

/** Gameplay effects unlocked by the command ladder. Effects are cumulative. */
export interface RankPerkBonuses {
  startSupplyBonus: number;
  towerDamageMul: number;
  integrityBonus: number;
  abilityCooldownMul: number;
  waveRewardMul: number;
  baseHardpointsBonus: number;
  swapCreditBonus: number;
  droneWingBonus: number;
  towerRangeMul: number;
}

export function getRankPerkBonuses(rank: number): RankPerkBonuses {
  const safeRank = Math.max(1, Math.floor(rank));
  return {
    startSupplyBonus: safeRank >= 1 ? 5 : 0,
    towerDamageMul: safeRank >= 5 ? 1.03 : 1,
    integrityBonus: safeRank >= 10 ? 1 : 0,
    abilityCooldownMul: safeRank >= 15 ? 0.96 : 1,
    waveRewardMul: safeRank >= 20 ? 1.08 : 1,
    baseHardpointsBonus: safeRank >= 25 ? 1 : 0,
    swapCreditBonus: safeRank >= 30 ? 0.05 : 0,
    droneWingBonus: safeRank >= 35 ? 1 : 0,
    towerRangeMul: safeRank >= 40 ? 1.1 : 1,
  };
}

/** Campaign unlock cadence for the advanced tower families. */
const MISSION_TOWER_UNLOCKS: Record<string, string[]> = {
  kappa: ['vulcan'],
  helios: ['sabre'],
  cinder: ['tesla'],
  meridian: ['cryo'],
  ashfall: ['dronehive'],
};

export interface SlotInfo {
  index: number;
  exists: boolean;
  rank: number;
  xp: number;
  difficulty: 'normal' | 'hard' | 'nightmare';
  missions: number;
  timestamp: number;
}

export interface LeaderboardEntry {
  id: string;
  commander: string;
  mapId: string;
  mapName: string;
  difficulty: 'normal' | 'hard' | 'nightmare';
  score: number;
  waves: number;
  integrity: number;
  stars: number;
  timestamp: number;
}

export interface ActiveRunSnapshot {
  missionId: string;
  difficulty: 'normal' | 'hard' | 'nightmare';
  survival: boolean;
  waveIndex: number;
  /** A mid-wave checkpoint restarts that wave so active enemies do not vanish. */
  waveActive?: boolean;
  supply: number;
  integrity: number;
  maxIntegrity: number;
  score: number;
  baseLevel: number;
  gameSpeed: number;
  simElapsed: number;
  realElapsed: number;
  towers: { id: string; padId: number; stage: number; priority: string; secondary?: string | null; secondaryLevel?: number; secondaryInvested?: number }[];
  /** Treasure sites secured during this sortie, retained across checkpoints. */
  claimedTreasures?: number[];
  /** Random hidden treasure assignments, retained so a resume keeps its luck. */
  treasureSites?: { id: number; padId: number; amount: number; kind: 'supply' | 'research' }[];
  savedAt: number;
}

export function getClearStars(win: boolean, integrity: number, maxIntegrity: number): number {
  if (!win || maxIntegrity <= 0 || integrity <= 0) return 0;
  const ratio = integrity / maxIntegrity;
  if (ratio >= 1) return 3;
  if (ratio >= 0.66) return 2;
  return 1;
}

export function formatClearStars(stars: number): string {
  const safe = Math.max(0, Math.min(3, Math.floor(stars)));
  return `${'★'.repeat(safe)}${'☆'.repeat(3 - safe)}`;
}

export interface GameSettings {
  sfx: boolean;
  music: boolean;
  voice: boolean;
  chatty: 'normal' | 'sparse' | 'warnings' | 'off';
  shake: boolean;
  slowmo: boolean;
  damageNumbers: boolean;
  grid: boolean;
  scanlines: boolean;
  autoWaves: boolean;
  particles: 'low' | 'medium' | 'high';
  sfxVolume: number;
  musicVolume: number;
  voiceVolume: number;
  scaleMode: 'fit' | 'fill';
  hudScale: number;
  uiTextScale: number;
  cursorSize: number;
  mobileContrast: boolean;
  /** Developer-only tuning; ignored by battles while devMode is off. */
  devMode: boolean;
  devEnemyHpMultiplier: number;
  devCreditMultiplier: number;
  devStartingSupply: number;
  devSquadSize: number;
  devBaseIntegrityMultiplier: number;
  devBreachDamageMultiplier: number;
  /** Per-map route weights used by developer playtests. */
  devMapLaneWeights: Record<string, number[]>;
}

export interface SaveData {
  version: number;
  /** Lightweight local profile identity used on scoreboards. */
  commanderName: string;
  rank: number;
  xp: number;
  difficulty: 'normal' | 'hard' | 'nightmare';
  researchPoints: number;
  research: string[];
  unlockedEnemies: string[];
  unlockedTowers: string[];
  unlockedAbilities: string[];
  bestScores: Record<string, number>;
  leaderboards: Record<string, LeaderboardEntry[]>;
  survivalBest: Record<string, { waves: number; score: number; time: number }>;
  settings: GameSettings;
  missionsComplete: string[];
  /** Campaign missions the player has attempted but not cleared yet. */
  missionsFailed: string[];
  /** Last in-progress sortie, checkpointed for the menu Continue action. */
  activeRun?: ActiveRunSnapshot;
  achievements: string[];
  /** Achievement ids that have been seen on the Awards screen. */
  awardsSeen: string[];
  /** Highest difficulty tier reached for skill-based achievements. */
  achievementStars: Record<string, number>;
  stats: {
    kills: number;
    artilleryKills: number;
    thermalKills: number;
    energyKills: number;
    ballisticKills: number;
    maxWave: number;
    bossesDefeated: number;
    perfectMissions: number;
    abilitiesUsed: number;
  };
}

const defaultSave = (): SaveData => ({
  version: SAVE_VERSION,
  commanderName: '',
  rank: 1,
  xp: 0,
  difficulty: 'normal',
  researchPoints: 0,
  research: [],
  unlockedEnemies: [],
  unlockedTowers: TEST_UNLOCK_ALL ? [...TOWER_ORDER] : ['ballistic', 'thermal', 'artillery', 'energy'],
  unlockedAbilities: ['airstrike', 'barrier', 'drone', 'emp'],
  bestScores: {},
  leaderboards: { overall: [] },
  survivalBest: {},
  settings: {
    sfx: true,
    music: true,
    voice: true,
    chatty: 'normal',
    shake: false,
    slowmo: true,
    damageNumbers: true,
    grid: true,
    scanlines: true,
    autoWaves: true,
    particles: 'high',
    sfxVolume: 0.8,
    musicVolume: 0.8,
    voiceVolume: 0.9,
    // FIT keeps every HUD control inside the viewport. Individual scenery
    // plates use a small crop so ultrawide displays still feel immersive.
    scaleMode: 'fit',
    hudScale: 1,
    uiTextScale: 1,
    cursorSize: 0.75,
    mobileContrast: true,
    devMode: false,
    devEnemyHpMultiplier: 1,
    devCreditMultiplier: 1,
    devStartingSupply: 0,
    devSquadSize: 1,
    devBaseIntegrityMultiplier: 1,
    devBreachDamageMultiplier: 1,
    devMapLaneWeights: {},
  },
  missionsComplete: [],
  missionsFailed: [],
  activeRun: undefined,
  achievements: [],
  awardsSeen: [],
  achievementStars: {},
  stats: {
    kills: 0,
    artilleryKills: 0,
    thermalKills: 0,
    energyKills: 0,
    ballisticKills: 0,
    maxWave: 0,
    bossesDefeated: 0,
    perfectMissions: 0,
    abilitiesUsed: 0,
  },
});

export class SaveManager {
  data: SaveData;

  constructor() {
    this.data = this.load();
  }

  load(): SaveData {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return defaultSave();
      const parsed = JSON.parse(raw) as SaveData;
      if (parsed.version !== SAVE_VERSION) {
        // Soft migrate: keep rank/xp, reset unknown keys
        return { ...defaultSave(), rank: parsed.rank ?? 1, xp: parsed.xp ?? 0 };
      }
      const base = defaultSave();
      const data = {
        ...base,
        ...parsed,
        settings: { ...base.settings, ...(parsed.settings ?? {}) },
        stats: { ...base.stats, ...(parsed.stats ?? {}) },
        leaderboards: { ...base.leaderboards, ...(parsed.leaderboards ?? {}) },
      };
      data.researchPoints = normalizeResearchPoints(data.researchPoints ?? 0);
      if (TEST_UNLOCK_ALL) {
        for (const towerId of TOWER_ORDER) {
          if (!data.unlockedTowers.includes(towerId)) data.unlockedTowers.push(towerId);
        }
      }
      // Older profiles already have campaign completions, so backfill their
      // earned tower licences rather than unexpectedly locking them out.
      for (const missionId of data.missionsComplete) {
        for (const towerId of MISSION_TOWER_UNLOCKS[missionId] ?? []) {
          if (!data.unlockedTowers.includes(towerId)) data.unlockedTowers.push(towerId);
        }
      }
      data.rank = Math.min(MAX_COMMAND_RANK, Math.max(1, Math.floor(data.rank ?? 1)));
      if (data.rank >= MAX_COMMAND_RANK) data.xp = Math.min(Math.max(0, data.xp ?? 0), this.rankThreshold(MAX_COMMAND_RANK));
      return data;
    } catch {
      return defaultSave();
    }
  }

  save(): void {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
      if (this.data.commanderName) this.saveProfileSnapshot(this.data.commanderName, this.data);
    } catch {
      /* ignore quota */
    }
  }

  reset(): void {
    this.data = defaultSave();
    this.save();
  }

  // ---- Save slots ------------------------------------------------------

  slotKey(index: number): string {
    return `${SLOT_PREFIX}${index}`;
  }

  slotInfo(index: number): SlotInfo {
    try {
      const raw = localStorage.getItem(this.slotKey(index));
      if (!raw) return { index, exists: false, rank: 1, xp: 0, difficulty: 'normal', missions: 0, timestamp: 0 };
      const parsed = JSON.parse(raw) as { data?: SaveData; timestamp?: number };
      const d = parsed.data;
      if (!d) return { index, exists: false, rank: 1, xp: 0, difficulty: 'normal', missions: 0, timestamp: 0 };
      return {
        index,
        exists: true,
        rank: Math.min(MAX_COMMAND_RANK, d.rank ?? 1),
        xp: d.xp ?? 0,
        difficulty: d.difficulty ?? 'normal',
        missions: d.missionsComplete?.length ?? 0,
        timestamp: parsed.timestamp ?? 0,
      };
    } catch {
      return { index, exists: false, rank: 1, xp: 0, difficulty: 'normal', missions: 0, timestamp: 0 };
    }
  }

  listSlots(): SlotInfo[] {
    return Array.from({ length: SLOT_COUNT }, (_, i) => this.slotInfo(i));
  }

  saveToSlot(index: number): void {
    try {
      localStorage.setItem(
        this.slotKey(index),
        JSON.stringify({ version: SAVE_VERSION, data: this.data, timestamp: Date.now() }),
      );
    } catch {
      /* ignore quota */
    }
  }

  loadFromSlot(index: number): boolean {
    try {
      const raw = localStorage.getItem(this.slotKey(index));
      if (!raw) return false;
      const parsed = JSON.parse(raw) as { data?: SaveData };
      if (!parsed.data) return false;
      const base = defaultSave();
      this.data = {
        ...base,
        ...parsed.data,
        settings: { ...base.settings, ...(parsed.data.settings ?? {}) },
        stats: { ...base.stats, ...(parsed.data.stats ?? {}) },
      };
      this.data.researchPoints = normalizeResearchPoints(this.data.researchPoints ?? 0);
      if (TEST_UNLOCK_ALL) {
        for (const towerId of TOWER_ORDER) {
          if (!this.data.unlockedTowers.includes(towerId)) this.data.unlockedTowers.push(towerId);
        }
      }
      for (const missionId of this.data.missionsComplete) {
        for (const towerId of MISSION_TOWER_UNLOCKS[missionId] ?? []) {
          if (!this.data.unlockedTowers.includes(towerId)) this.data.unlockedTowers.push(towerId);
        }
      }
      this.data.rank = Math.min(MAX_COMMAND_RANK, Math.max(1, Math.floor(this.data.rank ?? 1)));
      if (this.data.rank >= MAX_COMMAND_RANK) this.data.xp = Math.min(Math.max(0, this.data.xp ?? 0), this.rankThreshold(MAX_COMMAND_RANK));
      this.save();
      return true;
    } catch {
      return false;
    }
  }

  deleteSlot(index: number): void {
    try {
      localStorage.removeItem(this.slotKey(index));
    } catch {
      /* ignore */
    }
  }

  private profileStorageKey(name: string): string {
    return `${PROFILE_PREFIX}${encodeURIComponent(name.trim().toLowerCase())}`;
  }

  private profileNamesFromIndex(): string[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(PROFILE_INDEX_KEY) ?? '[]');
      return Array.isArray(parsed)
        ? parsed.filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
        : [];
    } catch {
      return [];
    }
  }

  private rememberProfile(name: string): void {
    const cleaned = name.trim();
    if (!cleaned) return;
    const names = this.profileNamesFromIndex().filter((item) => item.toLowerCase() !== cleaned.toLowerCase());
    names.unshift(cleaned);
    localStorage.setItem(PROFILE_INDEX_KEY, JSON.stringify(names.slice(0, 24)));
  }

  private saveProfileSnapshot(name: string, data: SaveData): void {
    const cleaned = name.trim();
    if (!cleaned) return;
    try {
      localStorage.setItem(this.profileStorageKey(cleaned), JSON.stringify({ version: SAVE_VERSION, data, timestamp: Date.now() }));
      this.rememberProfile(cleaned);
    } catch {
      /* ignore quota */
    }
  }

  listProfiles(): string[] {
    const names = this.profileNamesFromIndex();
    const add = (name: string | undefined) => {
      const cleaned = name?.trim();
      if (!cleaned || names.some((item) => item.toLowerCase() === cleaned.toLowerCase())) return;
      names.push(cleaned);
    };
    add(this.data.commanderName);
    // Ensure the currently active commander can always be selected again,
    // even on a fresh browser where the legacy save has not been indexed yet.
    if (this.data.commanderName && !localStorage.getItem(this.profileStorageKey(this.data.commanderName))) {
      this.saveProfileSnapshot(this.data.commanderName, this.data);
    }
    // Backfill snapshots from existing save slots created before profiles
    // existed, so those commanders appear in the selector immediately.
    for (let i = 0; i < SLOT_COUNT; i++) {
      try {
        const parsed = JSON.parse(localStorage.getItem(this.slotKey(i)) ?? 'null') as { data?: SaveData } | null;
        const slotData = parsed?.data;
        if (slotData?.commanderName) {
          add(slotData.commanderName);
          if (!localStorage.getItem(this.profileStorageKey(slotData.commanderName))) this.saveProfileSnapshot(slotData.commanderName, slotData);
        }
      } catch {
        /* ignore malformed legacy slot */
      }
    }
    return names;
  }

  loadProfile(name: string): boolean {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.profileStorageKey(name)) ?? 'null') as { data?: SaveData } | null;
      if (!parsed?.data) return false;
      const base = defaultSave();
      this.data = {
        ...base,
        ...parsed.data,
        settings: { ...base.settings, ...(parsed.data.settings ?? {}) },
        stats: { ...base.stats, ...(parsed.data.stats ?? {}) },
        leaderboards: { ...base.leaderboards, ...(parsed.data.leaderboards ?? {}) },
      };
      this.data.researchPoints = normalizeResearchPoints(this.data.researchPoints ?? 0);
      if (TEST_UNLOCK_ALL) {
        for (const towerId of TOWER_ORDER) {
          if (!this.data.unlockedTowers.includes(towerId)) this.data.unlockedTowers.push(towerId);
        }
      }
      for (const missionId of this.data.missionsComplete) {
        for (const towerId of MISSION_TOWER_UNLOCKS[missionId] ?? []) {
          if (!this.data.unlockedTowers.includes(towerId)) this.data.unlockedTowers.push(towerId);
        }
      }
      this.data.rank = Math.min(MAX_COMMAND_RANK, Math.max(1, Math.floor(this.data.rank ?? 1)));
      if (this.data.rank >= MAX_COMMAND_RANK) this.data.xp = Math.min(Math.max(0, this.data.xp ?? 0), this.rankThreshold(MAX_COMMAND_RANK));
      this.save();
      return true;
    } catch {
      return false;
    }
  }

  addXp(amount: number): number {
    if (this.data.rank >= MAX_COMMAND_RANK) {
      this.data.rank = MAX_COMMAND_RANK;
      this.data.xp = Math.min(Math.max(0, this.data.xp), this.rankThreshold(MAX_COMMAND_RANK));
      this.save();
      return 0;
    }
    this.data.xp += Math.max(0, amount);
    let ranked = 0;
    while (this.data.rank < MAX_COMMAND_RANK && this.data.xp >= this.rankThreshold(this.data.rank + 1)) {
      this.data.rank += 1;
      // Each promotion grants a meaningful research packet. Boss drops and
      // map-clear awards top this up, while the rank ladder carries the bulk
      // of the campaign's four-data-per-level pacing.
      this.adjustResearchPoints(scaleDataReward(5));
      ranked++;
    }
    if (this.data.rank >= MAX_COMMAND_RANK) this.data.xp = Math.min(this.data.xp, this.rankThreshold(MAX_COMMAND_RANK));
    this.save();
    return ranked;
  }

  adjustResearchPoints(amount: number): number {
    this.data.researchPoints = normalizeResearchPoints(this.data.researchPoints + amount);
    return this.data.researchPoints;
  }

  setCommanderName(name: string): void {
    const cleaned = name.trim().replace(/\s+/g, ' ').replace(/[^a-zA-Z0-9 _-]/g, '').slice(0, 18);
    const nextName = cleaned || 'ARC-CADET';
    const previousName = this.data.commanderName?.trim();
    // A different callsign is a new commander profile. Keep the old snapshot,
    // then start the new campaign from rank 1 with no research or clears.
    if (previousName && previousName.toLowerCase() !== nextName.toLowerCase()) {
      const oldData = this.data;
      this.saveProfileSnapshot(previousName, oldData);
      const fresh = defaultSave();
      fresh.commanderName = nextName;
      // Carry presentation preferences across profiles without carrying
      // campaign progress, research, scores, or an active run.
      fresh.settings = {
        ...fresh.settings,
        ...oldData.settings,
        // Developer tuning belongs to the old profile and must not make a
        // newly created commander start with free supply or custom weights.
        devMode: fresh.settings.devMode,
        devEnemyHpMultiplier: fresh.settings.devEnemyHpMultiplier,
        devCreditMultiplier: fresh.settings.devCreditMultiplier,
        devStartingSupply: fresh.settings.devStartingSupply,
        devSquadSize: fresh.settings.devSquadSize,
        devBaseIntegrityMultiplier: fresh.settings.devBaseIntegrityMultiplier,
        devBreachDamageMultiplier: fresh.settings.devBreachDamageMultiplier,
        devMapLaneWeights: fresh.settings.devMapLaneWeights,
      };
      this.data = fresh;
      this.save();
      return;
    }
    this.data.commanderName = nextName;
    this.save();
  }

  rankThreshold(rank: number): number {
    return Math.floor(100 * Math.pow(rank, 1.35));
  }

  recordScore(missionId: string, score: number): boolean {
    const prev = this.data.bestScores[missionId] ?? 0;
    if (score > prev) {
      this.data.bestScores[missionId] = score;
      this.save();
      return true;
    }
    return false;
  }

  recordLeaderboardEntry(entry: LeaderboardEntry): { mapRank: number; overallRank: number } {
    const insert = (key: string): number => {
      const list = [...(this.data.leaderboards[key] ?? [])].filter((item) => item.id !== entry.id);
      list.push(entry);
      list.sort((a, b) => b.score - a.score || b.timestamp - a.timestamp);
      this.data.leaderboards[key] = list.slice(0, 50);
      return list.findIndex((item) => item.id === entry.id) + 1;
    };
    const mapRank = insert(entry.mapId);
    const overallRank = insert('overall');
    this.save();
    return { mapRank, overallRank };
  }

  getLeaderboard(mapId: string | 'overall', limit = 10): LeaderboardEntry[] {
    return [...(this.data.leaderboards[mapId] ?? [])]
      .sort((a, b) => b.score - a.score || b.timestamp - a.timestamp)
      .slice(0, limit);
  }

  completeMission(missionId: string): void {
    this.data.missionsFailed = this.data.missionsFailed.filter((id) => id !== missionId);
    if (!this.data.missionsComplete.includes(missionId)) {
      this.data.missionsComplete.push(missionId);
    }
    for (const towerId of MISSION_TOWER_UNLOCKS[missionId] ?? []) this.unlockTower(towerId);
    this.save();
  }

  recordMissionFailure(missionId: string): void {
    if (!this.data.missionsComplete.includes(missionId) && !this.data.missionsFailed.includes(missionId)) {
      this.data.missionsFailed.push(missionId);
      this.save();
    }
  }

  saveActiveRun(snapshot: ActiveRunSnapshot): void {
    this.data.activeRun = snapshot;
    this.save();
  }

  clearActiveRun(): void {
    if (!this.data.activeRun) return;
    delete this.data.activeRun;
    this.save();
  }

  unlockEnemy(id: string): void {
    if (!this.data.unlockedEnemies.includes(id)) {
      this.data.unlockedEnemies.push(id);
      this.save();
    }
  }

  unlockTower(id: string): void {
    if (!this.data.unlockedTowers.includes(id)) {
      this.data.unlockedTowers.push(id);
      this.save();
    }
  }
}

export const saveManager = new SaveManager();
